import React from "react";
import { useState, useRef, KeyboardEvent, ClipboardEvent } from "react";

type Props = {
  value?: string[];
  onChange?: (phones: string[]) => void;
  maxPhones?: number;
  placeholder?: string;
  disabled?: boolean;
};

function formatPhone(raw: string): string {

  const digits = raw.replace(/\D/g, "").slice(0, 13);

  if (digits.length === 0) return "";
  if (digits.length <= 2) return `+${digits}`;
  if (digits.length <= 4) return `+${digits.slice(0, 2)} (${digits.slice(2)}`;
  if (digits.length <= 9) return `+${digits.slice(0, 2)} (${digits.slice(2, 4)}) ${digits.slice(4)}`;

  return `+${digits.slice(0, 2)} (${digits.slice(2, 4)}) ${digits.slice(4, 9)}-${digits.slice(9)}`;

}

function isValidPhone(phone: string): boolean {
  return phone.replace(/\D/g, "").length >= 10;
}

function uid(): string {
  return Math.random().toString(36).slice(2, 9);
}

export default function PhoneNumberInput({
  value,
  onChange,
  maxPhones = 5,
  placeholder = "+55 (54) 99999-9999",
  disabled = false,
}: Props) {
  const [phones, setPhones] = useState<string[]>(value ?? []);
  const [inputValue, setInputValue] = useState("");
  const [focused, setFocused] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const update = (next: string[]) => {
    setPhones(next);
    onChange?.(next);
  };

  const addPhone = (raw: string) => {
    const formatted = formatPhone(raw);
    if (!isValidPhone(formatted)) {
      setError("Número inválido. Digite pelo menos 10 dígitos.");
      return;
    }
    if (phones.some((p) => p === formatted.replace(/\D/g, ""))) {
      setError("Esse número já foi adicionado.");
      return;
    }
    if (phones.length >= maxPhones) {
      setError(`Máximo de ${maxPhones} números atingido.`);
      return;
    }
    setError("");
    update([...phones, formatted.replace(/\D/g, "")]);
    setInputValue("");
  };

  const removePhone = (id: string) => {
    update(phones.filter((p) => p !== id));
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (["Enter"].includes(e.key)) {
      e.preventDefault();
      if (inputValue.trim()) addPhone(inputValue.trim());
    }
    if (e.key === "Backspace" && !inputValue && phones.length > 0) {
      removePhone(phones[phones.length - 1]);
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const text = e.clipboardData.getData("text");
    text.split(/[\n,;]+/).map((s) => s.trim()).filter(Boolean).forEach(addPhone);
  };

  return (
    <div style={{ fontFamily: "system-ui, sans-serif" }}>
      <div
        onClick={() => inputRef.current?.focus()}
        style={{
          minHeight: 44,
          border: `1px solid ${error ? "#f87171" : "rgba(24,95,165,0.35)"}`,
          borderRadius: 10,
          background: disabled
            ? "#f5f5f3"
            : "#fff",
          display: "flex",
          flexWrap: "wrap",
          gap: 6,
          alignItems: "center",
          cursor: disabled ? "not-allowed" : "text",
          transition: "border-color 0.15s",
          padding: 5
        }}
      >
        {phones.map((phone) => (
          <span
            key={phone}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              padding: "3px 6px 3px 10px",
              borderRadius: 10,
              background: "#e6f1fb",
              border: "1px solid rgba(24,95,165,0.35)",
              fontSize: 13,
              color: "#0c447c",
              fontWeight: 500,
            }}
          >
            {formatPhone(phone)}
            {!disabled && (
              <button
                onClick={() => removePhone(phone)}
                aria-label={`Remover ${phone}`}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 16,
                  height: 16,
                  borderRadius: 10,
                  border: "none",
                  background: "none",
                  color: "#f87171",
                  cursor: "pointer",
                  fontSize: 14,
                  lineHeight: 1,
                  padding: 0,
                }}
              >
                ×
              </button>
            )}
          </span>
        ))}

        {phones.length < maxPhones && !disabled && (
          <input
            ref={inputRef}
            value={inputValue}
            placeholder={phones.length === 0 ? placeholder : "+55 (54) 99999-9999"}
            onChange={(e) => {
              setError("");
              setInputValue(formatPhone(e.target.value));
            }}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            onFocus={() => setFocused(true)}
            // onBlur={() => {
            //   setFocused(false);
            //   if (inputValue.trim()) addPhone(inputValue.trim());
            // }}
            style={{
              border: "1px solid rgba(24,95,165,0.35)",
              height: 35,
              borderRadius: 10,
              outline: "none",
              fontSize: 12,
              color: "#111",
              flex: 1,
              minWidth: 140,
              padding: "2px 10px",
              lineHeight: 25
            }}
          />
        )}
      </div>

      {error && (
        <div style={{ minHeight: 20, marginTop: 4 }}>
          <p style={{ fontSize: 12, color: "#f87171", margin: 0 }}>
            {error}
          </p>
        </div>
      )}

    </div>
  );
}